import { useState } from 'react'
import {
  ReturnOriginRemapChange,
  ReturnOriginRemapCreatedErpRow,
  ReturnOriginRemapRejectionCode,
  ReturnOriginRemapResponse,
} from '@/console/api/types'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { DataTable } from '@/console/ui/DataTable'
import { Stack } from '@/console/ui/Stack'
import {
  ORIGIN_REMAP_RESULT_KIND,
  OriginRemapResultKind,
  POST_APPLY_CHECKLIST,
  REJECTION_GUIDANCE,
  REJECTION_QUERY_SECTION,
  TRANSFER_BATCH_RETRY_GUIDANCE,
  classifyOriginRemapResult,
  formatChangeValues,
  isWaitingForTransferBatch,
} from './originRemap'
import { OriginRemapGuideLink } from './OriginRemapGuideLink'
import styles from './OriginRemap.module.css'

type ResponseProps = { response: ReturnOriginRemapResponse }

const ChangesTable = ({ changes }: { changes: ReturnOriginRemapChange[] }): JSX.Element => (
  <DataTable
    columns={[
      { key: 'table', label: 'table', mono: true },
      { key: 'id', label: 'id', mono: true },
      { key: 'before', label: 'before', mono: true },
      { key: 'after', label: 'after', mono: true },
    ]}
    rows={changes.map((change) => ({
      key: `${change.table}-${change.id}`,
      cells: { table: change.table, id: change.id, before: formatChangeValues(change.before), after: formatChangeValues(change.after) },
    }))}
  />
)

const CREATED_ERP_ROW_COLUMNS: (keyof ReturnOriginRemapCreatedErpRow)[] = [
  'id',
  'storeOrderNo',
  'storeOrderSeq',
  'sndSeq',
  'clsDate',
  'menge',
  'apiFlag',
]

const CreatedErpRowTable = ({ createdErpRow }: { createdErpRow: ReturnOriginRemapCreatedErpRow }): JSX.Element => (
  <DataTable
    columns={CREATED_ERP_ROW_COLUMNS.map((key) => ({ key, label: key, mono: true }))}
    rows={[{ key: String(createdErpRow.id), cells: { ...createdErpRow } }]}
  />
)

const ResponseMeta = ({ response }: ResponseProps): JSX.Element => (
  <p className={styles.meta}>
    requestId {response.requestId} · operator {response.operator}
  </p>
)

type CopyStatus = 'idle' | 'copied' | 'failed'

const COPY_STATUS_TEXT: Record<CopyStatus, string> = {
  idle: '',
  copied: '복사했어요.',
  failed: '복사하지 못했어요. 아래 응답 JSON 을 직접 복사하세요.',
}

/** 응답 전체가 되돌리기(역 UPDATE)의 근거라 복사가 실패해도 원문을 화면에 남긴다. */
const ResponseJsonCopy = ({ response }: ResponseProps): JSX.Element => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')
  const json = JSON.stringify(response, null, 2)

  const copy = (): void => {
    if (!navigator.clipboard) {
      setCopyStatus('failed')
      return
    }
    navigator.clipboard.writeText(json).then(
      () => setCopyStatus('copied'),
      () => setCopyStatus('failed'),
    )
  }

  return (
    <div>
      <div className={styles.actions}>
        <Button variant="line" size="sm" onClick={copy}>
          응답 JSON 복사
        </Button>
        <span className={styles.helper}>{COPY_STATUS_TEXT[copyStatus]}</span>
      </div>
      <details>
        <summary className={styles.helper}>응답 JSON 원문</summary>
        <pre className={styles.json}>{json}</pre>
      </details>
    </div>
  )
}

const RejectionQueryLink = ({ code }: { code: ReturnOriginRemapRejectionCode }): JSX.Element | null => {
  const section = REJECTION_QUERY_SECTION[code]
  if (!section) return null

  return (
    <>
      {' '}
      <OriginRemapGuideLink>가이드 {section} 조회</OriginRemapGuideLink>
    </>
  )
}

const RejectedView = ({ response }: ResponseProps): JSX.Element => {
  const { rejection } = response
  if (!rejection) {
    return <Callout tone="crit">거부 응답에 rejection 이 비어 있어요. 아무것도 바뀌지 않았어요.</Callout>
  }

  return (
    <Stack>
      <Callout tone="crit">
        <b>거부 · {rejection.code}</b> — 아무것도 바뀌지 않았어요. {REJECTION_GUIDANCE[rejection.code]}
        <RejectionQueryLink code={rejection.code} />
        <pre className={styles.detail}>{rejection.detail}</pre>
      </Callout>
      {isWaitingForTransferBatch(rejection) ? <Callout tone="warn">{TRANSFER_BATCH_RETRY_GUIDANCE}</Callout> : null}
      <ResponseMeta response={response} />
    </Stack>
  )
}

const PlannedView = ({ response }: ResponseProps): JSX.Element => (
  <Stack>
    <Callout tone="good">
      <b>판정 통과</b> — 아직 아무것도 바뀌지 않았어요. 아래 계획을 확인한 뒤 같은 입력 그대로 실행하세요.
    </Callout>
    <ChangesTable changes={response.changes} />
    <ResponseMeta response={response} />
  </Stack>
)

const AppliedView = ({ response }: ResponseProps): JSX.Element => (
  <Stack>
    <Callout tone="good">
      <b>실행 완료</b> — 세 테이블 보정과 ERP 반품행 생성이 커밋됐어요. 응답 JSON 을 티켓에 붙여 두세요.
    </Callout>
    <ResponseMeta response={response} />
    <ChangesTable changes={response.changes} />
    {response.createdErpRow ? <CreatedErpRowTable createdErpRow={response.createdErpRow} /> : null}
    <ResponseJsonCopy response={response} />
    <Callout tone="warn">
      <b>사후 확인 — 생략할 수 없어요</b>
      <ul className={styles.checklist}>
        {POST_APPLY_CHECKLIST.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Callout>
  </Stack>
)

const DryRunReturnedOnExecuteView = ({ response }: ResponseProps): JSX.Element => (
  <Stack>
    <Callout tone="crit">
      <b>버그 상태</b> — 실행을 요청했는데 판정(DRY_RUN_PASSED) 결과가 돌아왔어요. dryRun 값이 잘못 실려 나간 것이라 아무것도 바뀌지 않았어요. 다시
      실행하지 말고 응답 JSON 을 개발자에게 공유하세요.
    </Callout>
    <ResponseMeta response={response} />
    <ChangesTable changes={response.changes} />
    <ResponseJsonCopy response={response} />
  </Stack>
)

const VIEW_BY_RESULT_KIND = {
  [ORIGIN_REMAP_RESULT_KIND.PLANNED]: PlannedView,
  [ORIGIN_REMAP_RESULT_KIND.APPLIED]: AppliedView,
  [ORIGIN_REMAP_RESULT_KIND.REJECTED]: RejectedView,
  [ORIGIN_REMAP_RESULT_KIND.DRY_RUN_RETURNED_ON_EXECUTE]: DryRunReturnedOnExecuteView,
} satisfies Record<OriginRemapResultKind, (props: ResponseProps) => JSX.Element>

export type OriginRemapResultProps = ResponseProps & { dryRun: boolean }

/** 200 이 성공을 뜻하지 않는다 — `outcome` 과 호출 종류로 화면을 고른다. */
export const OriginRemapResult = ({ response, dryRun }: OriginRemapResultProps): JSX.Element => {
  const View = VIEW_BY_RESULT_KIND[classifyOriginRemapResult(response, dryRun)]
  return <View response={response} />
}
